import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { Box, Container, Grid, IconButton, Link, Stack, styled } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import {
  VmsType,
  getAllConfiguredVmsList,
  getVmsTypesList,
  deleteConfiguredVmsById,
  deleteVmsAccount,
  VmsAccount,
  VmsClient,
  pauseAccount,
  getCurrentWorkspace,
  getVmsDomain,
  resendVmsOtp,
} from 'wisestep-api';
import {
  IWorkspaceContext,
  WsButton,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import SettingsHeader from '../../../components/SettingsHeader/SettingsHeader';
import { WsBreadCrumbs } from '../../../components/WsBreadCrumbs';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { VmsConfigAction, useVmsAccessControl } from '../../../hooks/useVmsAccessControl';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { LayoutTheme } from '../utils/VmsHelper';

import AddEmailDialog from './components/addEmailDialog';
import ConfigureVmsDialog from './components/configureVmsDialog';
import LeftNavigation from './components/leftNavigation';
import UpdatePasswordDialog from './components/updatePasswordDialog';
import VerifyOtpDialog from './components/verifyOtpDialog';
import { configureVmsPayload } from './helper/payload';

const StyledButton = styled(WsButton)({
  backgroundColor: WsColors.White,
});

const VmsListing = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openConfigureVms, setOpenConfigureVms] = useState<boolean>(false);
  const [openUpdatePasswordDialog, setOpenUpdatePasswordDialog] = useState<boolean>(false);
  const [openAddEmail, setOpenAddEmail] = useState<boolean>(false);
  const [openVerifyOtp, setOpenVerifyOtp] = useState<boolean>(false);
  const [vmsTypeList, setVmsTypeList] = useState<VmsType[]>([]);
  const [configuredVmsList, setConfiguredVmsList] = useState<VmsAccount[]>([]);
  const [selectedVms, setSelectedVms] = useState<VmsType>({ name: '', label: '', text: '' });
  const [selectedVmsAccount, setSelectedVmsAccount] = useState<VmsAccount>(configureVmsPayload);
  const [currentWorkspace, setCurrentWorkspace] = useState<IWorkspaceContext>();
  const [vmsDomain, setVmsDomain] = useState<string>();
  const [editMode, setEditMode] = useState<boolean>(false);
  const { access, owns } = useVmsAccessControl([
    VmsConfigAction.EDIT_VMS_CONFIGURATION,
    VmsConfigAction.DELETE_VMS_CONFIGURATION,
  ]);
  const [editVms, deleteVms] = access;

  const emailBasedVms = ['PROWAND_PRO', 'BEELINE'];

  const openEdit = useCallback(() => {
    setOpenConfigureVms(true);
  }, []);
  const openUpdate = useCallback(() => {
    setOpenUpdatePasswordDialog(true);
  }, []);

  const configureVmsSuccessHandler = async () => {
    const response = await getAllConfiguredVmsList(selectedVms.name);
    setConfiguredVmsList(response.data);
  };

  const deleteVmsConfig = async (configId: string | '') => {
    if (configId) {
      await deleteConfiguredVmsById(configId);
      configureVmsSuccessHandler();
    }
  };

  const deleteVmsAccountById = async (configId: string | '') => {
    if (configId) {
      await deleteVmsAccount(configId);
      configureVmsSuccessHandler();
    }
  };

  const pauseVms = async (vmsAccount: VmsAccount, pause: boolean) => {
    vmsAccount.isPaused = pause;
    await pauseAccount(vmsAccount);
    configureVmsSuccessHandler();
  };

  const resendOtp = async (vmsAccount: VmsAccount) => {
    setCurrentVmsAccount(vmsAccount);
    try {
      await resendVmsOtp(vmsAccount);
      if (!openVerifyOtp) {
        setOpenVerifyOtp(true);
      }
      setNotification(success(`OTP sent to ${vmsAccount.whiteListEmail}`));
    } catch (error: any) {
      setNotification(failed('Something went wrong while sending OTP'));
    }
  };

  const openAddClient = () => {
    setOpenAddEmail(true);
  };
  const setCurrentVmsAccount = (vmsAccount: VmsAccount) => {
    setSelectedVmsAccount(vmsAccount);
  };
  const getCurrentVmsAccount = (): VmsAccount => {
    return selectedVmsAccount;
  };

  useEffect(() => {
    const getWorkspace = async () => {
      const response = await getCurrentWorkspace();
      setCurrentWorkspace(response.data);
    };
    const getDomain = async () => {
      const response = await getVmsDomain();
      setVmsDomain(response.data);
    };
    getWorkspace();
    getDomain();
  }, []);

  useEffect(() => {
    const getVmsTypes = async () => {
      const response = await getVmsTypesList();
      setVmsTypeList(response.data);
    };
    getVmsTypes();
  }, []);

  useEffect(() => {
    if (vmsTypeList.length > 0) {
      setSelectedVms(vmsTypeList[0]);
    }
  }, [vmsTypeList]);

  useEffect(() => {
    const getConfiguredVms = async () => {
      if (selectedVms.name) {
        const response = await getAllConfiguredVmsList(selectedVms.name);
        setConfiguredVmsList(response.data);
      }
    };
    getConfiguredVms();
  }, [selectedVms]);

  return (
    <LayoutTheme>
      <WsBreadCrumbs
        breadCrumbsList={getSettingBreadCrumbList(
          ISettingBreadCrumbTypes.APPS_AND_INTEGRATION,
          Pages['VMS_INTEGRATION']
        )}
      />
      <SettingsHeader title={'VMS Integration'} />
      <Grid container my={2}>
        <Grid item xs={12} container justifyContent="flex-end">
          <Stack direction="row" spacing={2} alignItems="right">
            {openConfigureVms && (
              <ConfigureVmsDialog
                open={openConfigureVms}
                editMode={editMode}
                selectedVmsAccount={selectedVmsAccount}
                handleClose={() => setOpenConfigureVms(false)}
                onSuccess={configureVmsSuccessHandler}
                selectedVms={selectedVms}
                openAddClient={openAddClient}
                setCurrentVmsAccount={setCurrentVmsAccount}
                getCurrentVmsAccount={() => getCurrentVmsAccount()}
                currentWorkspace={currentWorkspace}
                vmsDomain={vmsDomain}
              />
            )}
            {openUpdatePasswordDialog && (
              <UpdatePasswordDialog
                open={openUpdatePasswordDialog}
                selectedVmsAccount={selectedVmsAccount}
                handleClose={() => setOpenUpdatePasswordDialog(false)}
                getCurrentVmsAccount={() => getCurrentVmsAccount()}
              />
            )}
            {openVerifyOtp && (
              <VerifyOtpDialog
                handleSuccess={configureVmsSuccessHandler}
                openDialog={openVerifyOtp}
                currentVmsAccount={selectedVmsAccount}
                onClose={() => setOpenVerifyOtp(false)}
                resendOtp={(vmsAccount) => resendOtp(vmsAccount)}
              />
            )}
            <AddEmailDialog
              open={openAddEmail}
              handleClose={() => setOpenAddEmail(false)}
              onSuccess={configureVmsSuccessHandler}
              selectedVmsAccount={selectedVmsAccount}
            />
            {(owns || editVms) && (
              <WsButton
                variant="contained"
                size="large"
                onClick={() => {
                  setEditMode(false);
                  openEdit();
                }}
              >
                Configure VMS
              </WsButton>
            )}
          </Stack>
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <LeftNavigation
          vmsTypeList={vmsTypeList}
          setSelectedVms={setSelectedVms}
          selectedVms={selectedVms}
        />
        <Grid item xs={10}>
          <Container
            maxWidth="xl"
            sx={{
              minHeight: '400px',
              marginTop: configuredVmsList.length > 0 ? '15px' : '0px',
            }}
          >
            {configuredVmsList.length > 0 ? (
              <Grid>
                <Grid container sx={{ bgcolor: 'white' }} p={2}>
                  <Grid item xs={2} display="flex" justifyContent="flex-start" alignItems="center">
                    <WsTypography
                      bold
                      variant={WsTypoVariants.BODY2}
                      color={WsTypoColors.SECONDARY}
                    >
                      VMS Name
                    </WsTypography>
                  </Grid>
                  <Grid item xs={2} display="flex" justifyContent="flex-start" alignItems="center">
                    <WsTypography
                      bold
                      variant={WsTypoVariants.BODY2}
                      color={WsTypoColors.SECONDARY}
                    >
                      Whitelisted Email
                    </WsTypography>
                  </Grid>
                  <Grid item xs={1} display="flex" justifyContent="flex-start" alignItems="center">
                    <WsTypography
                      bold
                      variant={WsTypoVariants.BODY2}
                      color={WsTypoColors.SECONDARY}
                    >
                      Start/Stop
                    </WsTypography>
                  </Grid>
                  <Grid item xs={2} display="flex" justifyContent="flex-start" alignItems="center">
                    <WsTypography
                      bold
                      variant={WsTypoVariants.BODY2}
                      color={WsTypoColors.SECONDARY}
                    >
                      Client Name
                    </WsTypography>
                  </Grid>
                  <Grid item xs={1} display="flex" justifyContent="flex-start" alignItems="center">
                    <WsTypography
                      bold
                      variant={WsTypoVariants.BODY2}
                      color={WsTypoColors.SECONDARY}
                    >
                      Team
                    </WsTypography>
                  </Grid>
                  <Grid item xs={3} display="flex" justifyContent="flex-start" alignItems="center">
                    <WsTypography
                      bold
                      variant={WsTypoVariants.BODY2}
                      color={WsTypoColors.SECONDARY}
                    >
                      Job Owner
                    </WsTypography>
                  </Grid>
                  <Grid item xs={1} display="flex" justifyContent="center" alignItems="center">
                    <WsTypography
                      bold
                      variant={WsTypoVariants.BODY2}
                      color={WsTypoColors.SECONDARY}
                    >
                      Actions
                    </WsTypography>
                  </Grid>
                </Grid>
                {configuredVmsList.map((vmsObj: VmsAccount) => (
                  <Grid key={vmsObj.id + '_mainContainer'}>
                    <Grid
                      container
                      sx={{
                        marginTop: '10px',
                        border: '2px solid #EAEAF1',
                        bgcolor: 'white',
                      }}
                      minHeight="50px"
                    >
                      <Grid
                        xs={2}
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        bgcolor="#EAEAF1"
                        pl={2}
                      >
                        <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                          {vmsObj.accountName}
                        </WsTypography>
                      </Grid>
                      <Grid
                        xs={2}
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        bgcolor="#EAEAF1"
                        pl={2}
                      >
                        <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                          {vmsObj.whiteListEmail}
                        </WsTypography>
                      </Grid>
                      <Grid
                        xs={1}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        bgcolor="#EAEAF1"
                      >
                        {!vmsObj.isPaused && (
                          <WsButton
                            size="small"
                            variant="outlined"
                            startIcon={<StopCircleIcon />}
                            onClick={() => pauseVms(vmsObj, true)}
                            disabled={!(owns || editVms)}
                          >
                            {'Stop'}
                          </WsButton>
                        )}
                        {vmsObj.isPaused && (
                          <WsButton
                            size="small"
                            variant="outlined"
                            startIcon={<PlayCircleFilledWhiteIcon />}
                            onClick={() => pauseVms(vmsObj, false)}
                            disabled={!(owns || editVms)}
                          >
                            {'Start'}
                          </WsButton>
                        )}
                      </Grid>
                      <Grid
                        container
                        xs={2}
                        flexDirection="column"
                        alignItems="stretch"
                        justifyContent="space-around"
                        pl={2}
                      >
                        {vmsObj.accountClient?.map((clientObj: VmsClient, j: number) => (
                          <Grid key={'key_wl_' + j}>
                            <WsTypography
                              variant={WsTypoVariants.BODY1}
                              color={WsTypoColors.SECONDARY}
                            >
                              {clientObj.clientName}
                            </WsTypography>
                          </Grid>
                        ))}
                      </Grid>
                      <Grid
                        container
                        xs={1}
                        flexDirection="column"
                        alignItems="flex-start"
                        justifyContent="space-around"
                      >
                        {vmsObj.accountClient?.map((clientObj: VmsClient) => (
                          <Grid key={clientObj.id + '_vmsclient'}>
                            <WsTypography
                              variant={WsTypoVariants.BODY1}
                              color={WsTypoColors.SECONDARY}
                            >
                              {clientObj.team ? JSON.parse(clientObj.team)['name'] : ''}
                            </WsTypography>
                          </Grid>
                        ))}
                      </Grid>
                      <Grid
                        container
                        xs={3}
                        flexDirection="column"
                        alignItems="flex-start"
                        justifyContent="space-around"
                      >
                        {vmsObj.accountClient?.map((clientObj, j) => (
                          <Grid key={'key_wl_' + j}>
                            <WsTypography
                              variant={WsTypoVariants.BODY1}
                              color={WsTypoColors.SECONDARY}
                              bold={true}
                            >
                              {clientObj.jobOwner ? JSON.parse(clientObj.jobOwner)['email'] : ''}
                            </WsTypography>
                          </Grid>
                        ))}
                      </Grid>
                      <Grid
                        container
                        xs={1}
                        flexDirection="column"
                        alignItems="flex-start"
                        justifyContent="space-around"
                      >
                        {vmsObj.accountClient?.map(function (clientObj, k) {
                          return (
                            <Grid container key={'del_key_wl_' + k} justifyContent="center">
                              <IconButton
                                onClick={() => {
                                  if (clientObj.id !== undefined) {
                                    deleteVmsConfig(clientObj.id);
                                  }
                                }}
                                disabled={!(owns || deleteVms)}
                              >
                                <DeleteForeverIcon fontSize="medium" />
                              </IconButton>
                            </Grid>
                          );
                        })}
                        {(vmsObj.accountClient === null || vmsObj.accountClient?.length === 0) && (
                          <Grid container justifyContent="center">
                            <IconButton
                              onClick={() => {
                                if (vmsObj.id !== undefined) {
                                  deleteVmsAccountById(vmsObj.id);
                                }
                              }}
                              disabled={!(owns || deleteVms)}
                            >
                              <DeleteForeverIcon fontSize="medium" />
                            </IconButton>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                    <Grid container justifyContent="flex-end">
                      <Grid container item xs={7} justifyContent="center" mt={0.5}>
                        {(owns || editVms) && (
                          <>
                            <Grid item xs={4}>
                              {!vmsObj.isVerified ? (
                                <StyledButton
                                  variant="outlined"
                                  fullWidth={true}
                                  size="large"
                                  onClick={() => resendOtp(vmsObj)}
                                >
                                  Verify OTP
                                </StyledButton>
                              ) : (
                                <StyledButton
                                  disabled={!vmsObj.isVerified}
                                  variant="outlined"
                                  fullWidth={true}
                                  size="large"
                                  onClick={() => {
                                    setEditMode(true);
                                    setSelectedVmsAccount(vmsObj);
                                    openEdit();
                                  }}
                                >
                                  Edit Configuration
                                </StyledButton>
                              )}
                            </Grid>
                            <Grid item xs={3} mx={0.5}>
                              <StyledButton
                                disabled={!vmsObj.isVerified}
                                variant="outlined"
                                fullWidth={true}
                                size="large"
                                onClick={() => {
                                  setSelectedVmsAccount(vmsObj);
                                  setOpenAddEmail(true);
                                }}
                              >
                                Add Client
                              </StyledButton>
                            </Grid>
                            {!emailBasedVms.includes(selectedVms.name) && (
                              <Grid item xs={4} mr={3}>
                                <StyledButton
                                  disabled={!vmsObj.isVerified}
                                  variant="outlined"
                                  fullWidth={true}
                                  size="large"
                                  onClick={() => {
                                    setSelectedVmsAccount(vmsObj);
                                    openUpdate();
                                  }}
                                >
                                  Update Password
                                </StyledButton>
                              </Grid>
                            )}
                          </>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            ) : selectedVms.label == 'Covendis' || selectedVms.label == 'Vector' ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ minHeight: '60vh' }}
              >
                <Stack justifyContent="center" alignItems="center">
                  <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.SECONDARY}>
                    Coming Soon!
                  </WsTypography>
                </Stack>
              </Box>
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ minHeight: '60vh' }}
              >
                <Stack justifyContent="center" alignItems="center">
                  <WsTypography
                    variant={WsTypoVariants.SUB_TITLE}
                    color={WsTypoColors.SECONDARY}
                    cursor="pointer"
                  >
                    No VMS is configured for your account!
                  </WsTypography>
                  {(owns || editVms) && (
                    <Link onClick={openEdit} href="#">
                      Configure Now
                    </Link>
                  )}
                </Stack>
              </Box>
            )}
          </Container>
        </Grid>
      </Grid>
    </LayoutTheme>
  );
};
export default VmsListing;
